import { useState } from "react";
import { useQuery, useQueryClient } from "@tanstack/react-query";
import dayjs from "@/lib/bizday";
import api, { apiErr } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter, DialogDescription } from "@/components/ui/dialog";
import { BedDouble } from "lucide-react";
import { toast } from "sonner";
import { normCategory } from "@/lib/bookingRules";

// Allocate a room/kennel to a (pending) boarding booking. Server re-validates availability atomically.
export function AssignRoomButton({ booking, compact }) {
  const [open, setOpen] = useState(false);
  const [rid, setRid] = useState("");
  const [busy, setBusy] = useState(false);
  const qc = useQueryClient();
  const nights = Math.max(1, ...(booking.services || []).filter((s) => normCategory(s.category) === "boarding").map((s) => s.qty || 1));
  const start = dayjs(booking.start_time);
  const s = start.format("YYYY-MM-DDTHH:mm:ssZ");
  const e = start.add(nights, "day").format("YYYY-MM-DDTHH:mm:ssZ");
  const { data } = useQuery({
    queryKey: ["boarding-avail", s, e, booking.branch_id, booking.id], enabled: open,
    queryFn: async () => (await api.get("/boarding/availability", { params: { start_time: s, end_time: e, branch_id: booking.branch_id || "",
      customer_id: booking.customer_id, booking_id: booking.id,
      pet_ids: [...new Set((booking.services || []).filter((x) => normCategory(x.category) === "boarding" && x.pet_id).map((x) => x.pet_id))].join(",") } })).data,
  });
  const free = (data?.items || []).filter((r) => r.available);
  const save = async () => {
    setBusy(true);
    try {
      await api.patch(`/bookings/${booking.id}/resource`, { resource_id: rid });
      toast.success("Room assigned — you can now confirm the booking");
      qc.invalidateQueries({ queryKey: ["bookings"] });
      setOpen(false);
    } catch (err) { toast.error(apiErr(err)); } finally { setBusy(false); }
  };
  return (
    <>
      <button type="button" onClick={() => setOpen(true)} data-testid={`assign-room-${compact ? "m-" : ""}${booking.id}`}
        className="inline-flex items-center gap-1 rounded-md border border-amber-300 bg-amber-50 px-2 py-1 text-xs font-semibold text-amber-800 hover:bg-amber-100">
        <BedDouble className="h-3.5 w-3.5" /> Assign room
      </button>
      {open && (
        <Dialog open onOpenChange={setOpen}>
          <DialogContent className="bg-white max-w-sm" data-testid="assign-room-dialog">
            <DialogHeader><DialogTitle className="font-display">Assign room / kennel</DialogTitle></DialogHeader>
            <DialogDescription className="text-xs text-slate-500">{start.format("DD MMM YYYY")} · {nights} night{nights > 1 ? "s" : ""}. Only rooms free for the whole stay are listed.</DialogDescription>
            <select data-testid="assign-room-select" value={rid} onChange={(ev) => setRid(ev.target.value)} aria-label="Room or kennel"
              className="w-full rounded-lg border border-slate-200 px-3 py-2 text-sm bg-white">
              <option value="">Select a room…</option>
              {free.map((r) => <option key={r.id} value={r.id}>{r.name}{r.type ? ` · ${r.type}` : ""} · {r.occupied}/{r.capacity} pets</option>)}
            </select>
            {data && free.length === 0 && <p className="text-xs text-rose-600" data-testid="assign-room-none">No room is free for these dates.</p>}
            <DialogFooter>
              <Button variant="outline" onClick={() => setOpen(false)}>Cancel</Button>
              <Button onClick={save} disabled={!rid || busy} data-testid="assign-room-save" className="bg-slate-900 text-white">{busy ? "Saving…" : "Assign"}</Button>
            </DialogFooter>
          </DialogContent>
        </Dialog>
      )}
    </>
  );
}
